/**
 * Deterministic seeded PRNG.
 *
 * NULLRUN non-negotiable: ALL randomness in the engine flows through this.
 * Same seed + same call sequence => byte-identical results, on every platform.
 *
 * Algorithm: sfc32 (Small Fast Counting, 32-bit) seeded from a string via a
 * splitmix32-style expansion. sfc32 is fast, has a long period (~2^128), passes
 * PractRand, and — crucially — has a tiny, fully-serializable state (4 x uint32).
 *
 * The RNG state is a plain object so it serializes cleanly into the save file
 * and restores bit-identically. Never use Math.random/Date.now anywhere else.
 */

export interface RngState {
  a: number;
  b: number;
  c: number;
  d: number;
}

/** 32-bit string hash (FNV-1a) — used only to derive initial seed words. */
function hashStringToInt(str: string): number {
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    // FNV prime 16777619, done with Math.imul to stay in 32-bit.
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** splitmix32 — expands a single 32-bit seed into a stream of seed words. */
function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return function next(): number {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return (z ^ (z >>> 15)) >>> 0;
  };
}

/**
 * Build the initial RNG state from a seed string. Deterministic per string.
 * Two different strings should yield well-separated states.
 */
export function seedRng(seed: string): RngState {
  const base = hashStringToInt(seed.length === 0 ? "nullrun" : seed);
  const gen = splitmix32(base);
  // Burn one to decorrelate from base; fill four state words.
  gen();
  return {
    a: gen(),
    b: gen(),
    c: gen(),
    d: gen(),
  };
}

/**
 * Advance the state in place and return the next uint32.
 * sfc32 reference implementation.
 */
function nextUint32(s: RngState): number {
  // All ops kept in 32-bit via >>> 0 / Math.imul as appropriate.
  const t = (s.a + s.b) >>> 0;
  s.a = s.b ^ (s.b >>> 9);
  s.b = (s.c + (s.c << 3)) >>> 0;
  s.c = ((s.c << 21) | (s.c >>> 11)) >>> 0;
  s.d = (s.d + 1) >>> 0;
  const t2 = (t + s.d) >>> 0;
  s.c = (s.c + t2) >>> 0;
  return t2 >>> 0;
}

/**
 * A small RNG object that wraps a state. Pure-ish: it mutates only its own
 * internal state. Engine code constructs one of these from the run's RngState,
 * draws what it needs, then writes the updated state back into the new run
 * state. This keeps top-level reducers referentially honest while letting the
 * RNG be efficient.
 */
export class Rng {
  private s: RngState;

  constructor(state: RngState) {
    // Defensive copy so the caller's object is never mutated unexpectedly.
    this.s = { a: state.a, b: state.b, c: state.c, d: state.d };
  }

  static fromSeed(seed: string): Rng {
    return new Rng(seedRng(seed));
  }

  /** Snapshot the current state (serializable, restores bit-identically). */
  getState(): RngState {
    return { a: this.s.a, b: this.s.b, c: this.s.c, d: this.s.d };
  }

  /** Next float in [0, 1). 32 bits of entropy. */
  next(): number {
    return nextUint32(this.s) / 0x100000000;
  }

  /** Next raw uint32 (for hashing / integrity, not gameplay scaling). */
  nextUint32(): number {
    return nextUint32(this.s);
  }

  /**
   * Integer in [min, max] inclusive. Uses rejection sampling so the
   * distribution is exactly uniform (no modulo bias). min<=max required.
   */
  int(min: number, max: number): number {
    if (!Number.isInteger(min) || !Number.isInteger(max)) {
      throw new Error(`Rng.int requires integers, got ${min}, ${max}`);
    }
    if (min > max) {
      throw new Error(`Rng.int: min(${min}) > max(${max})`);
    }
    const range = max - min + 1;
    if (range <= 0) {
      throw new Error(`Rng.int: degenerate range`);
    }
    // Rejection-sample to remove modulo bias.
    const limit = Math.floor(0x100000000 / range) * range;
    let x = nextUint32(this.s);
    while (x >= limit) {
      x = nextUint32(this.s);
    }
    return min + (x % range);
  }

  /** Boolean with the given probability of true (default 0.5). */
  chance(p = 0.5): boolean {
    return this.next() < p;
  }

  /** Uniformly pick one element. Throws on empty array. */
  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) {
      throw new Error("Rng.pick: empty array");
    }
    return arr[this.int(0, arr.length - 1)]!;
  }

  /**
   * Weighted pick. weights[i] applies to arr[i]; must be same length and have
   * a positive total. Deterministic given the same state.
   */
  weightedPick<T>(arr: readonly T[], weights: readonly number[]): T {
    if (arr.length === 0) {
      throw new Error("Rng.weightedPick: empty array");
    }
    if (arr.length !== weights.length) {
      throw new Error("Rng.weightedPick: arr/weights length mismatch");
    }
    let total = 0;
    for (const w of weights) {
      if (w < 0) throw new Error("Rng.weightedPick: negative weight");
      total += w;
    }
    if (total <= 0) {
      throw new Error("Rng.weightedPick: total weight must be > 0");
    }
    // Use a uint32 mapped onto [0,total) for stability.
    let roll = this.next() * total;
    for (let i = 0; i < arr.length; i++) {
      roll -= weights[i]!;
      if (roll < 0) return arr[i]!;
    }
    // Floating point edge: return last.
    return arr[arr.length - 1]!;
  }

  /**
   * Fisher–Yates shuffle, returning a NEW array (does not mutate input).
   * Deterministic: same state + same input => same output order.
   */
  shuffle<T>(arr: readonly T[]): T[] {
    const out = arr.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const tmp = out[i]!;
      out[i] = out[j]!;
      out[j] = tmp;
    }
    return out;
  }
}
